import { WhiteTruck } from '@c/cards/MXListProductCard/TruckIcon';
import BaseModal from '@c/modals/BaseModal';
import ProductViewerModal from '@c/modals/ProductViewerModal';
import ProductConditions from '@c/ProductConditions';
import { useBuyerActionButtons } from '@features/shop';
import { ProductDocument } from 'models/product';
import { useAuth } from 'context/AuthContext';
import { Montserrat } from 'next/font/google';
import Link from 'next/link';
import { useState } from 'react';
import QuickViewMediaCarousel from './QuickViewMediaCarousel';
import BuyerActionButtons from '@c/product-details/BuyerActionButtons';
import ProductVariantSelector from '@c/product-details/ProductVariantSelector';
import ProductPriceRange from '@ui/ProductPriceRange';

const montserrat = Montserrat({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
});

const QuickViewModal = ({
  product,
  isOpen,
  dismiss,
  isSidebar,
}: {
  product: ProductDocument;
  isOpen: boolean;
  dismiss: () => void;
  isSidebar?: boolean;
}) => {
  const [openProductViewer, setOpenProductViewer] = useState(false);
  return (
    <>
      <BaseModal isOpen={isOpen} dismiss={dismiss} hideCloseIcon>
        <QuickViewContent
          product={product}
          dismiss={dismiss}
          isSidebar={isSidebar}
        />
      </BaseModal>

      <ProductViewerModal
        video={product.video}
        isOpen={openProductViewer}
        dismiss={() => setOpenProductViewer(false)}
        images={product.images}
      />
    </>
  );
};

export function QuickViewContent({
  product,
  dismiss,
  isSidebar,
}: {
  product: ProductDocument;
  dismiss?: () => void;
  isSidebar?: boolean;
}) {
  const { userDoc } = useAuth();
  const { selectedVariations, onSelection } = useBuyerActionButtons(product);

  return (
    <div
      className={`${montserrat.className} flex flex-col gap-y-8 self-stretch p-8`}
    >
      <div className="flex gap-x-16 p-2">
        <QuickViewMediaCarousel product={product} />

        <div className="flex flex-col gap-y-8">
          <div className="flex w-full items-start justify-between gap-x-8">
            <h1 className="line-clamp-2 w-[40rem] text-[2.5rem] font-bold">
              {product.title}
            </h1>

            <button type="button" onClick={dismiss} aria-label="Close">
              <CloseIcon className="h-12 w-12 cursor-pointer" />
            </button>
          </div>

          <div className="flex flex-col gap-y-2">
            <div className="flex items-center gap-x-6">
              <ProductPriceRange
                product={product}
                textSize="large"
                enableCouponDiscount={product.on_sale && !product.is_auction}
              />

              {product.is_flat_rate &&
              product.shipping_costs?.every(({ cost }) => cost === 0) &&
              !product.is_digital ? (
                <div className="rounded-2xl bg-black px-4 py-2">
                  <div className="flex items-center gap-[.5rem]">
                    <WhiteTruck className={'h-9 w-9'} />

                    <span className="text-[1.8rem] font-semibold italic text-white">
                      Free Shipping
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <ProductConditions product={product} />

          <div className="flex max-w-[50rem] flex-col gap-y-4">
            <ProductVariantSelector
              product={product}
              selectedVariations={selectedVariations ?? []}
              onSelection={onSelection}
            />

            <BuyerActionButtons
              product={product}
              selectedVariations={selectedVariations ?? []}
              dismissQuickView={dismiss}
              isSidebar={isSidebar}
            />
          </div>
        </div>
      </div>

      <div className="w-full border-t border-zinc-200 pt-8">
        <div className="flex w-full items-center justify-center">
          <Link
            href={`/product/${product.slug}`}
            className="flex items-start gap-x-4"
          >
            <span className="text-[1.8rem] font-semibold text-[#E20003]">
              View Listing
            </span>

            <ArrowTopRightIcon className="mt-[0.3rem] h-8 w-8" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default QuickViewModal;

const ArrowTopRightIcon = ({ className }: { className?: string }) => (
  <svg
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M3.79289 11.2929L3.08579 12L4.5 13.4142L5.20711 12.7071L3.79289 11.2929ZM5.20711 12.7071L13.2071 4.70711L11.7929 3.29289L3.79289 11.2929L5.20711 12.7071Z"
      fill="#C41719"
    />
    <path
      d="M6 4H12.5V10.5"
      stroke="#C41719"
      strokeWidth="2"
      strokeLinecap="square"
    />
  </svg>
);

const CloseIcon = ({ className }: { className?: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M18.75 6.81984L17.1802 5.25L12 10.4302L6.81984 5.25L5.25 6.81984L10.4302 12L5.25 17.1802L6.81984 18.75L12 13.5698L17.1802 18.75L18.75 17.1802L13.5698 12L18.75 6.81984Z"
      fill="black"
    />
  </svg>
);
